/**
 * @module pages/qpgen/QpgenGate
 * @description [QPGEN-1 Q3b] Who reaches the AI Question Papers screens (docs/QPGEN-1-PLAN.md §E). The server's routers
 * hold the real gates on every route — the role (SA, Admin, EMPLOYEE), then the school's `qpgen` flag, then each
 * service's own (a teacher's subject assignments, a paper's creator). The TEACHING category is checked HERE only: the
 * server's EMPLOYEE gate does not read it yet (pre-merge hardening owed, docs/KNOWN_DEV_DEBT.md QPGEN-1 Q3b).
 *
 * A school WITHOUT the flag has no nav entry (each shell's `feature: 'qpgen'`) and no route: every qpgen route renders
 * through <QpgenRoute>, which sends the person to their portal's home exactly as an unknown path does, and asks the
 * server nothing. The roles: the Super Admin, an Admin, and a TEACHING employee (a teacher's own papers; their books
 * only while the school lets teachers upload). A student, a parent and a non-teaching employee reach nothing.
 */
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSchoolContext } from '../../context/SchoolContext';

export type QpRole = 'SUPER_ADMIN' | 'ADMIN' | 'TEACHER';

/** The qpgen role of this login, or null when it has none (a student, a parent, a non-teaching employee). */
export function useQpRole(): { role: QpRole | null; userId: string } {
  const { user } = useAuth();
  const r = user?.role;
  const role: QpRole | null = r === 'SUPER_ADMIN' ? 'SUPER_ADMIN' : r === 'ADMIN' ? 'ADMIN'
    : r === 'EMPLOYEE' && user?.employeeCategory === 'TEACHING' ? 'TEACHER' : null;
  return { role, userId: user?.id ?? '' };
}

/** The portal's own path to the screens: /superadmin/question-papers, /admin/…, /teacher/…. */
export function qpBase(role: QpRole | null): string {
  return role === 'SUPER_ADMIN' ? '/superadmin/question-papers' : role === 'ADMIN' ? '/admin/question-papers' : '/teacher/question-papers';
}

function homeOf(role: string | undefined, category: string | undefined): string {
  if (role === 'SUPER_ADMIN') return '/superadmin';
  if (role === 'ADMIN') return '/admin';
  if (role === 'EMPLOYEE') return category === 'TEACHING' ? '/teacher' : '/employee';
  if (role === 'STUDENT') return '/student';
  return '/';
}

/** The route's door: the flag ON and a qpgen role, else the portal's home (no request is made). */
export function QpgenRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { hasFeature, isLoading } = useSchoolContext();
  const { role } = useQpRole();
  if (isLoading) return <div data-qpgen-gate="loading" role="status" aria-busy="true" style={{ minHeight: 120 }} />;
  if (!role || !hasFeature('qpgen')) return <Navigate to={homeOf(user?.role, user?.employeeCategory)} replace />;
  return <>{children}</>;
}

export default QpgenRoute;
